// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CapsuleButton } = require('client-v2/common/components/buttons')
const Modal = require('client-v2/common/components/modal')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const ContentAddModalArticlePreview = require('./components/article-preview')
const ContentAddModalUrlForm = require('./components/url-form')
const ContentAddModalUrlSubmitted = require('./components/url-submitted')

const bem = BemEntity('ContentAddModal')

function ContentAddModal({ className, ...props }) {
  const innerRef = React.useRef()
  const trailingStyle = useTrailingStyle(innerRef, [
    props.isArticleLoading,
    props.isCollectionsModalVisible,
    props.collectionsCount
  ])
  const trailingComponent = props.isCollectionsModalVisible
  ? ContentAddModalUrlSubmitted
  : ContentAddModalUrlForm
  return (
    tag(Modal, {
      className: classNames(
        className,
        bem.block()
      ),
      onDismiss: props.onDismiss
    },
      tag('div', {
        ref: innerRef,
        className: bem.element('inner')
      },
        tag(ContentAddModalArticlePreview, {
          className: bem.element('leading'),
          ...props
        }),
        tag(trailingComponent, {
          className: bem.element('trailing'),
          style: trailingStyle,
          ...props
        })
      ),
      tag(ContentAddModalFooter, props)
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')
  const ArticleModelRecord = require('client-v2/common/models/article/record')

  ContentAddModal.propTypes = {
    // Flags
    isArticleLoading: PropTypes.bool,
    isCollectionsModalVisible: PropTypes.bool,
    // Values
    article: PropTypes.shape(ArticleModelRecord.propTypes),
    className: PropTypes.string,
    collectionsCount: PropTypes.number,
    postHref: PropTypes.string,
    promo: PropTypes.shape({
      href: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    }),
    url: PropTypes.string,
    // Callbacks
    onDismiss: PropTypes.func
  }
}

module.exports = ContentAddModal

// CSS selectors

const trailingSelector = bem.elementSelector('trailing')
const footerSelector = bem.elementSelector('footer')

// React hooks

/**
 * Work-around for flexbox issue where the embedded LWE modal does not respect
 * the flex grow/shrink to fit inside the modal without either:
 *   1. Causing scrollbars within scrollbars, which is a terrible experience
 *   2. Requiring scrolling to the bottom of the collection list to see the footer
 *
 * Ideally we could use `calc` and `vh` units to accomplish this, but `vh` doesn't
 * work correctly on iOS, and there would need to be an unreasonable number of `calc`
 * permutations to work correctly with the responsive design of the modal.
 */
function useTrailingStyle(innerRef, deps) {
  const [ trailingHeight, setTrailingHeight ] = React.useState()
  const onResize = React.useCallback(
    () => {
      const innerEl = innerRef.current
      if (!innerEl) {
        return
      }
      const contentEl = innerEl.parentElement
      const modalEl = contentEl.parentElement
      const maxHeight = modalEl.offsetHeight - contentEl.offsetTop
      const trailingEl = contentEl.querySelector(trailingSelector)
      const footerEl = contentEl.querySelector(footerSelector)
      const trailingTop = trailingEl.offsetTop
      const footerHeight = footerEl ? footerEl.offsetHeight : 0
      const trailingHeightAvailable = maxHeight - trailingTop - footerHeight
      setTrailingHeight(trailingHeightAvailable)
    },
    [ innerRef, setTrailingHeight ]
  )
  useClientEffect(
    () => {
      if (!trailingHeight) {
        onResize()
      }
    },
    [ !trailingHeight, onResize ]
  )
  useClientEffect(
    () => {
      // Trigger on the trailing edge so that onResize is called after layout
      const onResizeThrottled = _.throttle(onResize, 16, { trailing: true })
      onResizeThrottled()
      window.addEventListener('resize', onResizeThrottled)
      return () => {
        window.removeEventListener('resize', onResizeThrottled)
        onResizeThrottled.cancel()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ onResize, ...deps ]
  )
  return React.useMemo(
    () => trailingHeight ? ({ maxHeight: trailingHeight }) : undefined,
    [ trailingHeight ]
  )
}

// Components

function ContentAddModalFooterPromo({
  href,
  type,
  onClick
}) {
  const verb = type === 'app'
  ? 'Download'
  : 'Install'
  const noun = type === 'app'
  ? 'Mix app'
  : 'Mix browser extension'
  return (
    tag('a', {
      className: bem.element('footer-promo'),
      href,
      onClick
    },
      verb, ' the ', noun, '.'
    )
  )
}

function ContentAddModalFooter({
  isCollectionsModalVisible,
  postHref,
  promo,
  onDismiss
}){
  return (
    tag('div', {
      className: bem.element('footer')
    },
      !!promo && (
        tag(ContentAddModalFooterPromo, promo)
      ),
      !!isCollectionsModalVisible && (
        tag('div', {
          className: bem.element('footer-actions')
        },
          tag(CapsuleButton, {
            className: bem.element('view-post'),
            href: postHref
          },
            'View post'
          ),
          tag(CapsuleButton, {
            className: bem.element('done'),
            onClick: onDismiss
          },
            'Done'
          )
        )
      )
    )
  )
}
